import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { PageLayout } from '../components/common/PageLayout';
import { DataTable } from '../components/common/DataTable';
import { MultiSelectListbox } from '../components/common/MultiSelectListbox';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import {
  useParameterSets,
  useUpdateParameterSet,
  useCreateParameterSet,
  useDeleteParameterSet,
  useLookups,
  ParameterSet,
  ParameterSetInput,
} from '../hooks/useCaapApi';
import { ColumnDef } from '@tanstack/react-table';

const listColumns: ColumnDef<ParameterSet, any>[] = [
  { accessorKey: 'STEP', header: 'Step #', size: 70 },
  { accessorKey: 'SavedName', header: 'Name' },
  {
    accessorKey: 'IsActive',
    header: 'Active',
    cell: ({ getValue }) => (
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${getValue() ? 'bg-green-500' : 'bg-border-default'}`} />
    ),
  },
];

function splitCSV(val: string | null): string[] {
  if (!val) return [];
  return val.split(',').map((s) => s.trim()).filter(Boolean);
}

function joinCSV(arr: string[]): string {
  return arr.join(',');
}

interface FormValues {
  SavedName: string;
  Schools: string[];
  AccountStatuses: string[];
  AccountConditions: string[];
  StudentStatuses: string[];
  NoPaymentsSinceBuffer: number;
  WithdrawnBeforeBuffer: number;
  ContractDateBuffer: number;
  CreditBalanceOnBuffer: number;
  MinimumCurrentBalance: number;
  MinimumPayoffBalance: number;
  MaximumPayoffBalance: number;
  Deferred: boolean;
  IsMonthly: boolean;
  IsWeekly: boolean;
  IsBiWeekly: boolean;
}

export const ParameterSetManager: React.FC = () => {
  const { data: params, isLoading } = useParameterSets();
  const lookups = useLookups();
  const updateMutation = useUpdateParameterSet();
  const createMutation = useCreateParameterSet();
  const deleteMutation = useDeleteParameterSet();

  const [editing, setEditing] = useState<ParameterSet | null>(null);
  const [creating, setCreating] = useState(false);

  const { register, handleSubmit, reset, control } = useForm<FormValues>();

  function openEdit(param: ParameterSet) {
    setCreating(false);
    setEditing(param);
    reset({
      SavedName: param.SavedName,
      Schools: splitCSV(param.Schools),
      AccountStatuses: splitCSV(param.AccountStatuses),
      AccountConditions: splitCSV(param.AccountConditions),
      StudentStatuses: splitCSV(param.StudentStatuses),
      NoPaymentsSinceBuffer: param.NoPaymentsSinceBuffer,
      WithdrawnBeforeBuffer: param.WithdrawnBeforeBuffer,
      ContractDateBuffer: param.ContractDateBuffer,
      CreditBalanceOnBuffer: param.CreditBalanceOnBuffer,
      MinimumCurrentBalance: param.MinimumCurrentBalance,
      MinimumPayoffBalance: param.MinimumPayoffBalance,
      MaximumPayoffBalance: param.MaximumPayoffBalance,
      Deferred: param.Deferred,
      IsMonthly: param.IsMonthly,
      IsWeekly: param.IsWeekly,
      IsBiWeekly: param.IsBiWeekly,
    });
  }

  function openCreate() {
    setEditing(null);
    setCreating(true);
    reset({
      SavedName: '',
      Schools: [],
      AccountStatuses: [],
      AccountConditions: [],
      StudentStatuses: [],
      NoPaymentsSinceBuffer: 0,
      WithdrawnBeforeBuffer: 0,
      ContractDateBuffer: 0,
      CreditBalanceOnBuffer: 1,
      MinimumCurrentBalance: 0,
      MinimumPayoffBalance: 0,
      MaximumPayoffBalance: 9999,
      Deferred: false,
      IsMonthly: true,
      IsWeekly: true,
      IsBiWeekly: true,
    });
  }

  async function onSubmit(values: FormValues) {
    const input: ParameterSetInput = {
      SavedName: values.SavedName,
      Schools: values.Schools.length > 0 ? joinCSV(values.Schools) : null,
      AccountStatuses: joinCSV(values.AccountStatuses),
      AccountConditions: joinCSV(values.AccountConditions),
      StudentStatuses: joinCSV(values.StudentStatuses),
      NoPaymentsSinceBuffer: Number(values.NoPaymentsSinceBuffer),
      WithdrawnBeforeBuffer: Number(values.WithdrawnBeforeBuffer),
      ContractDateBuffer: Number(values.ContractDateBuffer),
      CreditBalanceOnBuffer: Number(values.CreditBalanceOnBuffer),
      MinimumCurrentBalance: Number(values.MinimumCurrentBalance),
      MinimumPayoffBalance: Number(values.MinimumPayoffBalance),
      MaximumPayoffBalance: Number(values.MaximumPayoffBalance),
      Deferred: values.Deferred,
      IsMonthly: values.IsMonthly,
      IsWeekly: values.IsWeekly,
      IsBiWeekly: values.IsBiWeekly,
    };

    if (editing) {
      await updateMutation.mutateAsync({ id: editing.ID, input });
    } else {
      const nextStep = (params?.length ?? 0) + 1;
      input.Step = nextStep;
      await createMutation.mutateAsync(input);
    }
    setEditing(null);
    setCreating(false);
  }

  const schoolOptions = (lookups.schools.data ?? []).map((s) => ({
    value: String(s.ID),
    label: s.Name,
  }));
  const statusOptions = (lookups.accountStatuses.data ?? []).map((s) => ({
    value: String(s.ID),
    label: s.Name,
  }));
  const conditionOptions = (lookups.accountConditions.data ?? []).map((s) => ({
    value: String(s.ID),
    label: s.Name,
  }));
  const studentStatusOptions = (lookups.studentStatuses.data ?? []).map((s) => ({
    value: String(s.ID),
    label: s.Name,
  }));

  const showForm = editing || creating;

  return (
    <PageLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary">Parameter Sets</h1>
          <p className="mt-1 text-sm text-muted">
            Manage the 7 automated collection step parameter sets
          </p>
        </div>
        {!showForm && (
          <button
            onClick={openCreate}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Add Step
          </button>
        )}
      </div>

      {isLoading && <LoadingSpinner message="Loading parameter sets..." />}

      {params && !showForm && (
        <DataTable data={params} columns={listColumns} onRowClick={openEdit} pageSize={10} />
      )}

      {showForm && (
        <div className="rounded-lg border border-border-default bg-surface-alt p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-primary">
              {editing ? `Edit Step ${editing.STEP}: ${editing.SavedName}` : 'New Parameter Set'}
            </h2>
            <button
              onClick={() => { setEditing(null); setCreating(false); }}
              className="text-sm text-muted hover:text-secondary"
            >
              Cancel
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Step Name */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Step Name</label>
              <input
                {...register('SavedName', { required: true })}
                className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Multi-select dropdowns */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Controller
                name="Schools"
                control={control}
                render={({ field }) => (
                  <MultiSelectListbox
                    label="Schools"
                    options={schoolOptions}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="AccountStatuses"
                control={control}
                render={({ field }) => (
                  <MultiSelectListbox
                    label="Account Statuses"
                    options={statusOptions}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="AccountConditions"
                control={control}
                render={({ field }) => (
                  <MultiSelectListbox
                    label="Account Conditions"
                    options={conditionOptions}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="StudentStatuses"
                control={control}
                render={({ field }) => (
                  <MultiSelectListbox
                    label="Student Statuses"
                    options={studentStatusOptions}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/* Buffer Fields */}
            <div>
              <h3 className="text-sm font-medium text-secondary mb-2">Date Buffers (months)</h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <label className="block text-xs text-muted mb-1">No Payments Since</label>
                  <input
                    type="number"
                    {...register('NoPaymentsSinceBuffer', { valueAsNumber: true })}
                    className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Withdrawn Before</label>
                  <input
                    type="number"
                    {...register('WithdrawnBeforeBuffer', { valueAsNumber: true })}
                    className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Contract Date</label>
                  <input
                    type="number"
                    {...register('ContractDateBuffer', { valueAsNumber: true })}
                    className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Credit Balance On</label>
                  <input
                    type="number"
                    {...register('CreditBalanceOnBuffer', { valueAsNumber: true })}
                    className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Balance Range */}
            <div>
              <h3 className="text-sm font-medium text-secondary mb-2">Balance Range ($)</h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-xs text-muted mb-1">Min Current Balance</label>
                  <input
                    type="number"
                    {...register('MinimumCurrentBalance', { valueAsNumber: true })}
                    className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Min Payoff Balance</label>
                  <input
                    type="number"
                    {...register('MinimumPayoffBalance', { valueAsNumber: true })}
                    className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Max Payoff Balance</label>
                  <input
                    type="number"
                    {...register('MaximumPayoffBalance', { valueAsNumber: true })}
                    className="w-full rounded-md border border-border-default bg-surface-alt text-primary px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Boolean Toggles */}
            <div>
              <h3 className="text-sm font-medium text-secondary mb-2">Options</h3>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-2 text-sm text-secondary">
                  <input type="checkbox" {...register('IsMonthly')} className="rounded border-border-default" />
                  <span>Monthly</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-secondary">
                  <input type="checkbox" {...register('IsWeekly')} className="rounded border-border-default" />
                  <span>Weekly</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-secondary">
                  <input type="checkbox" {...register('IsBiWeekly')} className="rounded border-border-default" />
                  <span>Bi-Weekly</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-secondary">
                  <input type="checkbox" {...register('Deferred')} className="rounded border-border-default" />
                  <span>Deferred</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3 pt-2">
              <button
                type="submit"
                disabled={updateMutation.isPending || createMutation.isPending}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {updateMutation.isPending || createMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={async () => {
                    if (window.confirm(`Delete step "${editing.SavedName}"?`)) {
                      await deleteMutation.mutateAsync(editing.ID);
                      setEditing(null);
                    }
                  }}
                  className="rounded-md bg-red-50 dark:bg-red-950 px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900"
                >
                  Delete
                </button>
              )}
            </div>

            {(updateMutation.isError || createMutation.isError) && (
              <div className="rounded-md bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">
                Save failed: {((updateMutation.error || createMutation.error) as Error)?.message}
              </div>
            )}
          </form>
        </div>
      )}
    </PageLayout>
  );
};
